import { InputHTMLAttributes } from "react";
import { IScreenByType } from "./../hooks/useMedia";
import { IPage } from "./../store/pages/types";
import { PopupTypes } from "./../store/types/Popup";
import { AddressInput, NameInput, Selected } from "./../store/fields/boomtypes";
import { IIsInputSelected } from "./../store/selected/types";
import { FieldTypes, IField } from "./../store/fields/types";

export const withoutBlurItems: PopupTypes[] = [PopupTypes.TEXT_EDITOR];

export interface IItemPosition {
    top: number;
    height: number;
    bottom: number;
}

export const getItemPosition = (id: IPage["_id"]): IItemPosition => {
    const element = document.getElementById(`page-${id}`);
    const itemPosition: IItemPosition = {
        top: 0,
        height: 0,
        bottom: 0,
    };

    if (element) {
        const itemElementPosition = element.getBoundingClientRect();
        const { top, height } = itemElementPosition || {};

        itemPosition.top = top;
        itemPosition.height = height;
        itemPosition.bottom = top + height;
    }

    return itemPosition;
};

export const getScreenType = (): IScreenByType => {
    if (window.matchMedia("(max-width: 767px)").matches) return IScreenByType.MOBILE;
    else if (window.matchMedia("(max-width: 1023px)").matches) return IScreenByType.DESKTOP_SMALL;
    else if (window.matchMedia("(max-width: 1439px)").matches) return IScreenByType.DESKTOP_MIDDLE;
    else if (window.matchMedia("(max-width: 1920px)").matches) return IScreenByType.DESKTOP;
    else return IScreenByType.DESKTOP_BIG;
};

const checkMiddleNameValue = (customId: string, id: string) => {
    let validateMiddleName = false;
    const isHaveMiddleName = customId.includes(NameInput.MIDDlE);
    if (isHaveMiddleName) {
        const formValues = window.__current_form_state.values;
        const customId = `fields.${id.slice(0, id.indexOf("."))}.isMiddleName`;
        const customIdKeys: string[] = customId.split(".") || [];
        let value: any = null;

        customIdKeys.forEach(key => {
            if (value === null) {
                value = formValues[key];
            } else {
                if (value) value = value[key];
            }
        });
        validateMiddleName = value;
    }

    return { validateMiddleName, isHaveMiddleName };
};

const checkAddressValueWithOrder = (customId: string, id: string) => {
    let validateAddressInput = false;
    const isHaveAddressInput = Object.values(AddressInput).find(inputType => customId.includes(inputType));
    if (isHaveAddressInput) {
        const formValues = window.__current_form_state.values;
        const customId = `fields.${id.slice(0, id.indexOf("."))}.order`;
        const customIdKeys: string[] = customId.split(".") || [];
        let value: any = null;

        customIdKeys.forEach(key => {
            if (value === null) {
                value = formValues[key];
            } else {
                if (value) value = value[key];
            }
        });
        validateAddressInput = value.includes(isHaveAddressInput);
    }
    return { isHaveAddressInput, validateAddressInput };
};

const getSelectedInputsIds = (isSelected: IIsInputSelected, key: string): string[] => {
    let selectedIds: string[] = [];
    let selectedType = isSelected?.includes(",")
        ? Selected.MULTI
        : isSelected === Selected.All
        ? Selected.All
        : isSelected;

    switch (selectedType) {
        case Selected.MULTI:
            selectedIds = isSelected?.split(",") || [];
            break;
        case Selected.All:
            const fields = window.__current_form_state.fields;

            fields.forEach(({ id: customId }: { id: string }) => {
                const keyIndexInCustom = customId.lastIndexOf(key);

                if (
                    customId.includes("fields") &&
                    keyIndexInCustom === customId.length - key.length &&
                    keyIndexInCustom !== -1
                ) {
                    const removedFieldsKey = customId.replace("fields.", "");
                    const id = removedFieldsKey.slice(
                        0,
                        removedFieldsKey.indexOf(".", removedFieldsKey.indexOf(".") + 1),
                    );

                    const { isHaveMiddleName, validateMiddleName } = checkMiddleNameValue(customId, id);
                    const { isHaveAddressInput, validateAddressInput } = checkAddressValueWithOrder(customId, id);

                    if (!selectedIds.includes(id)) {
                        if (
                            (!isHaveMiddleName || (isHaveMiddleName && validateMiddleName)) &&
                            (!isHaveAddressInput || (isHaveAddressInput && validateAddressInput))
                        )
                            selectedIds.push(id);
                    }
                }
            });
            break;
        default:
            selectedIds.push(`${isSelected}`);
            break;
    }

    return selectedIds;
};

export const isMixedTracker = (
    isSelected: IIsInputSelected,
    key: string,
): { isMix: boolean; value: any; values: any[] } => {
    const formValues = window.__current_form_state.values;
    const values: any = [];
    const isLabel = key.includes("label");
    const selectedIds: string[] = getSelectedInputsIds(isSelected, key);

    selectedIds?.forEach(selectedId => {
        const isLabelAndInputInSelectId = isLabel && !selectedId.includes("label");
        const customId = `fields.${
            isLabelAndInputInSelectId
                ? selectedId.slice(0, selectedId.indexOf(".")) + ".label"
                : !isLabel && `${selectedId}.input`
        }.${isLabel ? key.replace("label.", "") : key}`;
        const customIdKeys: string[] = customId.split(".") || [];
        let value: any = null;

        customIdKeys.forEach(key => {
            if (value === null) {
                value = formValues[key];
            } else {
                if (value) value = value[key];
            }
        });

        if (!values.includes(value) && value) values.push(value);
    });
    const isMix = values.length > 1;

    return { isMix, value: isMix ? null : values[0], values: values };
};

const setWheightByElement = (id: string, checkedValue: InputHTMLAttributes<Event>["value"] | null) => {
    const inputElement = document.getElementById(id);
    if (inputElement && inputElement.clientWidth) {
        const newWidth = Math.floor(inputElement.clientWidth);

        if (checkedValue === null) {
            return newWidth;
        } else if (checkedValue !== newWidth) {
            return "";
        }
    }

    return checkedValue;
};

export const getCheckedValue = (
    isSelectAll: boolean,
    isMultiSelect: boolean,
    isInputSelected: IIsInputSelected,
    inputType: string | null,
    id: IField["_id"],
    fields: IField[],
): InputHTMLAttributes<Event>["value"] => {
    let checkedValue: InputHTMLAttributes<Event>["value"] | null = null;

    if (isSelectAll) {
        fields.forEach(({ _id, type }) => {
            switch (type) {
                case FieldTypes.NAME:
                    Object.values(NameInput).forEach(inputType => {
                        checkedValue = setWheightByElement(`${_id}.${inputType}input`, checkedValue);
                    });
                    break;
                case FieldTypes.ADDRESS:
                    Object.values(AddressInput).forEach(inputType => {
                        checkedValue = setWheightByElement(`${_id}.${inputType}input`, checkedValue);
                    });
                    break;
                default:
                    checkedValue = setWheightByElement(`${_id}.input`, checkedValue);
                    break;
            }
        });
    } else if (isMultiSelect) {
        const selectedIds = isInputSelected?.split(",") || [];

        selectedIds.forEach(customId => {
            checkedValue = setWheightByElement(`${customId}.input`, checkedValue);
        });
    } else {
        checkedValue = setWheightByElement(`${id}${inputType ? "." + inputType : ""}.input`, checkedValue);
    }

    if (checkedValue === null || checkedValue === undefined) checkedValue = "";

    return checkedValue;
};

export enum BrowserTypes {
    OPERA,
    FIREFOX,
    SAFARI,
    INTERNET_EXPLORER,
    EDGE,
    CHROME,
}

export const browserChecker = () => {
    // Opera 8.0+
    // @ts-ignore
    if ((!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(" OPR/") >= 0)
        return BrowserTypes.OPERA;

    // Firefox 1.0+
    // @ts-ignore
    if (typeof InstallTrigger !== "undefined") return BrowserTypes.FIREFOX;

    // Safari 3.0+ "[object HTMLElementConstructor]"
    if (
        // @ts-ignore
        /constructor/i.test(window.HTMLElement) ||
        (function (p) {
            return p.toString() === "[object SafariRemoteNotification]";
            // @ts-ignore
        })(!window["safari"] || (typeof safari !== "undefined" && safari.pushNotification))
    )
        return BrowserTypes.SAFARI;

    // Internet Explorer 6-11
    // @ts-ignore
    if (/*@cc_on!@*/ false || !!document.documentMode) return BrowserTypes.INTERNET_EXPLORER;

    // Edge 20+
    // @ts-ignore
    if (!!window.StyleMedia) return BrowserTypes.EDGE;

    // Chrome 1 - 71
    // @ts-ignore
    if (!!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)) return BrowserTypes.CHROME;
};
